/** @jsxImportSource @emotion/react */
'use memo'

import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { DATA_GRID_TOKENS as T } from 'src/components/ts-components/DataGrid/tokens'
import { ORG_REGISTRATION_ROW_HEIGHT } from '../../constants/listLayout'

interface Props {
  rows: number
  /** The header band — on the cold load; not for the next page's rows. */
  withHeader?: boolean
}

/**
 * The list view's skeleton: the table's header and rows at the real sizes —
 * chevron, logo and name, sport, status pill, two date lines, the countdown,
 * the action — so the table landing in its place doesn't move anything.
 */
const OrgRegistrationTableSkeleton = ({ rows, withHeader = false }: Props) => {
  'use memo'

  return (
    <Frame aria-hidden='true' framed={withHeader}>
      {withHeader && (
        <Header>
          <span />
          <Bar barWidth='96px' barHeight='14px' />
          <Bar barWidth='56px' barHeight='14px' />
          <Bar barWidth='64px' barHeight='14px' />
          <Bar barWidth='56px' barHeight='14px' />
          <Bar barWidth='88px' barHeight='14px' />
          <Bar barWidth='88px' barHeight='14px' />
          <Bar barWidth='72px' barHeight='14px' />
          <Bar barWidth='64px' barHeight='14px' />
        </Header>
      )}
      {Array.from({ length: rows }, (_, index) => (
        <Row key={index}>
          <Bar barWidth='16px' barHeight='16px' />
          <Identity>
            <Bar barWidth='32px' barHeight='32px' />
            <Bar barWidth='160px' barHeight='14px' />
          </Identity>
          <Bar barWidth='96px' barHeight='14px' />
          <Lines>
            <Bar barWidth='104px' barHeight='12px' />
            <Bar barWidth='80px' barHeight='12px' />
          </Lines>
          <Bar barWidth='72px' barHeight='24px' pill />
          <Lines>
            <Bar barWidth='64px' barHeight='12px' />
            <Bar barWidth='96px' barHeight='12px' />
          </Lines>
          <Bar barWidth='96px' barHeight='14px' />
          <Lines>
            <Bar barWidth='56px' barHeight='12px' />
            <Bar barWidth='112px' barHeight='12px' />
          </Lines>
          <Bar barWidth='112px' barHeight='32px' />
        </Row>
      ))}
    </Frame>
  )
}

export default OrgRegistrationTableSkeleton

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

/* The columns at the grid's own widths, so a row lands where its bars were. */
const COLUMNS =
  '48px 260px minmax(150px, 1fr) minmax(180px, 1.2fr) repeat(2, minmax(150px, 1fr)) minmax(200px, 1fr) minmax(170px, 1fr) 150px'

const Frame = styled.div<{ framed: boolean }>`
  overflow: hidden;
  border: ${({ framed }) =>
    framed ? `${T.borderWidth}px solid ${T.wrapperBorderColor}` : 'none'};
  border-radius: ${T.radius}px;
  background: ${T.background};
`

const Header = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  height: 48px;
  padding: 0 ${T.cellHorizontalPadding}px;
  gap: ${T.cellHorizontalPadding}px;
  border-bottom: 1px solid ${T.rowBorderColor};
`

const Row = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  height: ${ORG_REGISTRATION_ROW_HEIGHT}px;
  padding: 0 ${T.cellHorizontalPadding}px;
  gap: ${T.cellHorizontalPadding}px;
  border-bottom: 1px solid ${T.rowBorderColor};

  &:last-of-type {
    border-bottom: none;
  }
`

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Bar = styled.div<{ barWidth: string; barHeight: string; pill?: boolean }>`
  width: ${({ barWidth }) => barWidth};
  max-width: 100%;
  height: ${({ barHeight }) => barHeight};
  border-radius: ${({ pill }) => (pill ? '999px' : '8px')};
  background-color: ${T.rowHover};
  animation: ${pulse} 1.4s ease-in-out infinite;
`
